import { AccountAutomatedReferralsApi } from "api/account-automated-referrals.api";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { keyBy } from "lodash";
import { IObservableArray, makeAutoObservable, observable } from "mobx";
import { AppStore } from "store/App.store";
import {
  TAutomatedReferralOfficeRelationData,
  TAutomatedReferralSyncOfficeExtendedData,
} from "types/automated-referrals.type";

const automatedReferralsApi = new AccountAutomatedReferralsApi();

export class AccountIntegrationAutomatedReferralsStore {
  private readonly root: AppStore;
  private readonly accountId: number;
  private readonly accountAutomatedReferralsOfficeRelations: IObservableArray<
    TAutomatedReferralOfficeRelationData
  >;
  private syncStatusFilter: boolean;

  constructor(root: AppStore, accountId: number) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.root = root;
    this.accountId = accountId;
    this.accountAutomatedReferralsOfficeRelations = observable.array<
      TAutomatedReferralOfficeRelationData
    >();
    this.syncStatusFilter = false;
  }

  get getSyncStatusFilter() {
    return this.syncStatusFilter;
  }

  get automatedOfficeReferralRelationsMapByLeadProOfficeId() {
    return keyBy(
      this.accountAutomatedReferralsOfficeRelations,
      relation => relation.officeId
    );
  }

  get filteredAutomatedReferralsOfficeRelationData() {
    return this.automatedReferralsOfficeRelationData.filter(
      syncData =>
        !this.syncStatusFilter ||
        syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
    );
  }

  get automatedReferralsOfficeRelationData() {
    const accountStore = this.root.userAccountsStore.userAccountsMap[
      this.accountId
    ];
    const leadproOffices =
      accountStore.accountOfficesStore.accountOfficesDataArray;
    const automatedOfficeReferralRelationsMapByLeadProOfficeId = this
      .automatedOfficeReferralRelationsMapByLeadProOfficeId;

    return leadproOffices.map(office => {
      let pairedAutomatedReferralOfficeId =
        automatedOfficeReferralRelationsMapByLeadProOfficeId[office.id]
          ?.membersHubOfficeId;
      let syncStatus = !!pairedAutomatedReferralOfficeId
        ? IntegrationEntitySyncStatusEnum.SYNCED
        : IntegrationEntitySyncStatusEnum.NOT_SYNCED;

      return {
        officeId: office.id,
        officeName: office.name,
        membersHubOfficeId: pairedAutomatedReferralOfficeId,
        syncStatus: syncStatus,
      };
    });
  }

  public setSyncStatusFilter(value: boolean) {
    this.syncStatusFilter = value;
  }

  private setAccountAutomatedReferralOfficeRelations(
    officeRelations: TAutomatedReferralOfficeRelationData[]
  ) {
    this.accountAutomatedReferralsOfficeRelations.replace(officeRelations);
  }

  public async fetchAccountAutomatedReferralsOfficeRelations() {
    const data = await automatedReferralsApi.fetchAccountOfficeRelations(
      this.accountId
    );
    this.setAccountAutomatedReferralOfficeRelations(data);
  }

  public async updateAccountAutomatedReferralsOfficeRelations(
    syncedPairs: TAutomatedReferralSyncOfficeExtendedData[]
  ) {
    const relationData: TAutomatedReferralOfficeRelationData[] = syncedPairs.map(
      relation => ({
        officeId: relation.officeId,
        membersHubOfficeId: relation.membersHubOfficeId || null,
      })
    );
    await automatedReferralsApi.updateSyncedOffices(
      this.accountId,
      relationData
    );

    await this.fetchAccountAutomatedReferralsOfficeRelations();
  }
}
